<script setup lang="ts">
import type Offering from '../../../js/dto/offering/Offering.js';
import { computed, onMounted, ref } from 'vue';
import { SphCheckbox } from '@/components/base/checkbox';
import { SphDialog } from '@/components/base/dialog';
import TableColumn from '../../../js/dto/offering/TableColumn.js';

interface Column {
    key: string
    title: string
}

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    showDialog: boolean
    offering: Offering
    userId: number
    tableColumns: Column[]
    type: string
}>();

const emit = defineEmits<{
    change: [columns: string[]]
    closeDialog: []
}>();

const filteredColumns = ref<Column[]>([]);
const selectedKeys = ref<string[]>([]);

const isSelected = (key: string) => selectedKeys.value.includes(key);

function toggle(key: string) {
    selectedKeys.value = isSelected(key)
        ? selectedKeys.value.filter((selectedKey) => selectedKey !== key)
        : [...selectedKeys.value, key];
}

const localStorageKey = computed(() => {
    let type = props.type;
    if (props.offering.isExamForDogs()) {
        type = `dogExam.${type}`;
    }
    return `clbt.tableConfig.offering.${type}.${props.userId}`;
});

const tableColumnSplitIndex = computed(() => Math.ceil(filteredColumns.value.length / 2));
const tableColumnsLeft = computed(() => filteredColumns.value.slice(0, tableColumnSplitIndex.value));
const tableColumnsRight = computed(() => filteredColumns.value.slice(tableColumnSplitIndex.value));

function loadConfiguration(): string[] | null {
    const stored = window.localStorage.getItem(localStorageKey.value);
    return stored === null ? null : JSON.parse(stored);
}

function initConfiguration() {
    if (loadConfiguration() === null) {
        const columns = [];
        if (props.offering.isExamForDogs()) {
            columns.push(TableColumn.DOG);
        }
        if (props.type === TableColumn.TABLE_TYPE_PARTICIPANTS) {
            columns.push(
                TableColumn.NAME,
                TableColumn.CONTRACT_STATUS,
                TableColumn.AGE,
                TableColumn.EMAIL,
                TableColumn.PHONE,
                TableColumn.PHONE_MOBILE,
                TableColumn.EMERGENCY_CONTACT,
                TableColumn.BOOKED_AT,
                TableColumn.ATTENDEE_STATUS,
                TableColumn.COMMENT,
            );
        } else if (props.type === TableColumn.TABLE_TYPE_CANCELLATIONS) {
            columns.push(
                TableColumn.NAME,
                TableColumn.CONTRACT_STATUS,
                TableColumn.AGE,
                TableColumn.EMAIL,
                TableColumn.PHONE,
                TableColumn.PHONE_MOBILE,
                TableColumn.EMERGENCY_CONTACT,
                TableColumn.BOOKED_AT,
                TableColumn.CANCELED_AT,
                TableColumn.ATTENDEE_STATUS,
                TableColumn.COMMENT,
            );
        } else if (props.type === TableColumn.TABLE_TYPE_WAITLIST) {
            columns.push(
                TableColumn.NAME,
                TableColumn.CONTRACT_STATUS,
                TableColumn.AGE,
                TableColumn.EMAIL,
                TableColumn.PHONE,
                TableColumn.PHONE_MOBILE,
                TableColumn.EMERGENCY_CONTACT,
                TableColumn.BOOKED_AT,
                TableColumn.INFORMED_AT,
                TableColumn.STATUS_WAITLIST,
                TableColumn.COMMENT,
            );
        } else if ([TableColumn.TABLE_TYPE_ACCEPTED, TableColumn.TABLE_TYPE_REJECTED, TableColumn.TABLE_TYPE_UNSURE].includes(props.type)) {
            columns.push(
                TableColumn.NAME,
                TableColumn.CONTRACT_STATUS,
                TableColumn.AGE,
                TableColumn.EMAIL,
                TableColumn.PHONE,
                TableColumn.PHONE_MOBILE,
                TableColumn.EMERGENCY_CONTACT,
                TableColumn.REPLIED_AT,
                TableColumn.ATTENDEE_STATUS,
                TableColumn.COMMENT,
            );
        } else if (props.type === TableColumn.TABLE_TYPE_ATTENDANCE) {
            window.localStorage.setItem(localStorageKey.value, JSON.stringify([]));
            return;
        }
        if (columns.length > 0) {
            window.localStorage.setItem(localStorageKey.value, JSON.stringify(columns));
        }
    }
}

function filterColumns(): Column[] {
    if (props.offering.useWaitList) {
        if (props.type === TableColumn.TABLE_TYPE_PARTICIPANTS || props.type === TableColumn.TABLE_TYPE_CANCELLATIONS) {
            return props.tableColumns.filter((column) => !TableColumn.WAITLIST_COLUMNS.includes(column.key));
        }
        if (props.type === TableColumn.TABLE_TYPE_WAITLIST) {
            return props.tableColumns.filter((column) => column.key !== TableColumn.CANCELED_AT);
        }
    }
    return props.tableColumns;
}

function initColumns() {
    filteredColumns.value = filterColumns();
    const selected = loadConfiguration();
    if (selected === null) {
        selectedKeys.value = filteredColumns.value.map((column) => column.key);
        return;
    }
    selectedKeys.value = filteredColumns.value
        .filter((column) => selected.includes(column.key))
        .map((column) => column.key);
}

function saveConfiguration() {
    const selected = [...selectedKeys.value];
    window.localStorage.setItem(localStorageKey.value, JSON.stringify(selected));
    emit('change', selected);
}

function onSubmit(event: { done: (success: boolean) => void }) {
    saveConfiguration();
    event.done(true);
}

function onUpdateState(value: boolean) {
    if (!value) {
        initColumns();
        emit('closeDialog');
    }
}

onMounted(() => {
    initConfiguration();
    initColumns();
});

// Parent tables read the stored configuration through this template ref
// (e.g. `this.$refs.tableConfiguration.loadConfiguration()`), so it must stay exposed.
defineExpose({ loadConfiguration });
</script>

<template>
    <SphDialog
        :state="showDialog"
        variant="form"
        :title="$t('offering.tableConfig')"
        :submit-button-label="$t('saveAndClose')"
        :cancel-button-label="$t('cancel')"
        @update:state="onUpdateState"
        @submit="onSubmit"
    >
        <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters kgr-grid--bigGutters">
            <div class="kgr-grid__cell">
                <label
                    v-for="column in tableColumnsLeft"
                    :key="column.key"
                    class="flex items-center gap-2 mb-2.5 cursor-pointer"
                >
                    <SphCheckbox
                        :model-value="isSelected(column.key)"
                        @update:model-value="toggle(column.key)"
                    />
                    <span>{{ $t(column.title) }}</span>
                </label>
            </div>
            <div class="kgr-grid__cell">
                <label
                    v-for="column in tableColumnsRight"
                    :key="column.key"
                    class="flex items-center gap-2 mb-2.5 cursor-pointer"
                >
                    <SphCheckbox
                        :model-value="isSelected(column.key)"
                        @update:model-value="toggle(column.key)"
                    />
                    <span>{{ $t(column.title) }}</span>
                </label>
            </div>
        </div>
    </SphDialog>
</template>
